"use client";

import { HardDrive, RefreshCw, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

interface StorageItem {
  key: string;
  value: string;
  sizeBytes: number;
  sizeKB: string;
}

interface StorageStats {
  localStorage: StorageItem[];
  sessionStorage: StorageItem[];
  totalLocalStorageBytes: number;
  totalSessionStorageBytes: number;
  lastRefresh: Date;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / k ** i).toFixed(2)} ${sizes[i]}`;
}

function getStorageSize(value: string): number {
  return new Blob([value]).size;
}

function refreshStorage(): StorageStats {
  const localStorageItems: StorageItem[] = [];
  const sessionStorageItems: StorageItem[] = [];
  let totalLocalBytes = 0;
  let totalSessionBytes = 0;

  // Get localStorage items
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key) || "";
        const sizeBytes = getStorageSize(value);
        totalLocalBytes += sizeBytes;
        localStorageItems.push({
          key,
          value,
          sizeBytes,
          sizeKB: formatBytes(sizeBytes),
        });
      }
    }
  } catch (e) {
    // localStorage might be disabled
  }

  // Get sessionStorage items
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key) {
        const value = sessionStorage.getItem(key) || "";
        const sizeBytes = getStorageSize(value);
        totalSessionBytes += sizeBytes;
        sessionStorageItems.push({
          key,
          value,
          sizeBytes,
          sizeKB: formatBytes(sizeBytes),
        });
      }
    }
  } catch (e) {
    // sessionStorage might be disabled
  }

  // Sort by size descending
  localStorageItems.sort((a, b) => b.sizeBytes - a.sizeBytes);
  sessionStorageItems.sort((a, b) => b.sizeBytes - a.sizeBytes);

  return {
    localStorage: localStorageItems,
    sessionStorage: sessionStorageItems,
    totalLocalStorageBytes: totalLocalBytes,
    totalSessionStorageBytes: totalSessionBytes,
    lastRefresh: new Date(),
  };
}

export function StorageTab() {
  const [storage, setStorage] = useState<StorageStats | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [fillAmount, setFillAmount] = useState<string>("4.5");
  const [isFilling, setIsFilling] = useState(false);
  const [fillProgress, setFillProgress] = useState<{
    totalWritten: number;
    chunkIndex: number;
    targetBytes: number;
  } | null>(null);
  const [fillResult, setFillResult] = useState<{
    type: "success" | "quota_exceeded" | "error";
    message: string;
  } | null>(null);
  const [clearResult, setClearResult] = useState<string | null>(null);

  const refresh = () => {
    setStorage(refreshStorage());
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 2000); // Auto-refresh every 2 seconds
    return () => clearInterval(interval);
  }, []);

  if (!storage) {
    return <div className="p-4 text-gray-500">Loading storage info...</div>;
  }

  const allItems = [
    ...storage.localStorage.map((item) => ({
      ...item,
      source: "localStorage" as const,
    })),
    ...storage.sessionStorage.map((item) => ({
      ...item,
      source: "sessionStorage" as const,
    })),
  ].sort((a, b) => b.sizeBytes - a.sizeBytes);

  const totalBytes =
    storage.totalLocalStorageBytes + storage.totalSessionStorageBytes;

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">
            Browser Storage
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Monitor localStorage and sessionStorage usage
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Storage Summary */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <HardDrive className="h-4 w-4 text-blue-500" />
          Storage Summary
        </div>

        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-500 block text-xs">Total Storage</span>
            <span className="text-lg font-bold text-gray-900">
              {formatBytes(totalBytes)}
            </span>
          </div>
          <div>
            <span className="text-gray-500 block text-xs">localStorage</span>
            <span className="text-lg font-bold text-gray-900">
              {formatBytes(storage.totalLocalStorageBytes)}
            </span>
            <span className="text-xs text-gray-500">
              ({storage.localStorage.length} items)
            </span>
          </div>
          <div>
            <span className="text-gray-500 block text-xs">sessionStorage</span>
            <span className="text-lg font-bold text-gray-900">
              {formatBytes(storage.totalSessionStorageBytes)}
            </span>
            <span className="text-xs text-gray-500">
              ({storage.sessionStorage.length} items)
            </span>
          </div>
        </div>
      </div>

      {/* Storage Items by Size */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-700">
            Items by Size (Largest First)
          </h4>
          <span className="text-xs text-gray-400">
            Updated: {storage.lastRefresh.toLocaleTimeString()}
          </span>
        </div>

        {allItems.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
            <div className="text-gray-600 text-sm">No storage items</div>
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {allItems.map((item) => {
              const isExpanded = expandedKey === `${item.source}:${item.key}`;
              const percentage =
                totalBytes > 0 ? (item.sizeBytes / totalBytes) * 100 : 0;

              return (
                <div
                  key={`${item.source}:${item.key}`}
                  className="rounded-lg border border-gray-200 overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedKey(
                        isExpanded ? null : `${item.source}:${item.key}`,
                      )
                    }
                    className="w-full p-3 hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded text-gray-600">
                            {item.source}
                          </span>
                          <span className="font-mono text-sm text-gray-900 truncate">
                            {item.key}
                          </span>
                        </div>
                        <div className="mt-2 w-full bg-gray-200 rounded-full h-1">
                          <div
                            className="bg-blue-500 h-1 rounded-full transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-sm font-bold text-gray-900">
                          {item.sizeKB}
                        </div>
                        <div className="text-xs text-gray-500">
                          {percentage.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  </button>

                  {/* Expanded Value Preview */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 bg-gray-50 p-3 space-y-2">
                      <div className="text-xs font-medium text-gray-700">
                        Value Preview:
                      </div>
                      <div className="bg-white rounded border border-gray-200 p-2 max-h-32 overflow-y-auto">
                        <pre className="text-xs text-gray-600 break-words whitespace-pre-wrap font-mono">
                          {item.value.length > 500
                            ? `${item.value.substring(0, 500)}...`
                            : item.value}
                        </pre>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(item.value);
                          }}
                          className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                        >
                          Copy Value
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (item.source === "localStorage") {
                              localStorage.removeItem(item.key);
                            } else {
                              sessionStorage.removeItem(item.key);
                            }
                            refresh();
                          }}
                          className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors flex items-center gap-1"
                        >
                          <Trash2 className="h-3 w-3" />
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Fill Storage for Testing */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
        <div className="text-sm font-medium text-amber-900">
          Test localStorage Quota Issue
        </div>
        <p className="text-xs text-amber-800">
          Fill localStorage to near-capacity to test if quota exhaustion causes
          the loadHrts hang. Run a check-in flow after filling.
        </p>
        <div className="space-y-3">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label
                htmlFor="fill-amount-input"
                className="text-xs text-amber-900 block mb-1"
              >
                Fill amount (MB, default 4.5 for 5MB iOS limit):
              </label>
              <input
                id="fill-amount-input"
                type="number"
                min="0.1"
                step="0.5"
                value={fillAmount}
                onChange={(e) => setFillAmount(e.target.value)}
                disabled={isFilling}
                className="w-full px-2 py-1 text-xs border border-amber-300 rounded bg-white disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>
            <button
              type="button"
              onClick={async () => {
                setIsFilling(true);
                setFillResult(null);
                setClearResult(null);
                try {
                  const mb = Number.parseFloat(fillAmount);
                  if (Number.isNaN(mb) || mb <= 0) {
                    setFillResult({
                      type: "error",
                      message: "Invalid size - must be greater than 0",
                    });
                    setIsFilling(false);
                    return;
                  }

                  const bytesToFill = mb * 1024 * 1024;
                  const chunkSize = 1024 * 100; // 100KB chunks
                  const fakeData = "x".repeat(chunkSize);
                  let totalWritten = 0;
                  let chunkIndex = 0;

                  while (totalWritten < bytesToFill) {
                    try {
                      localStorage.setItem(
                        `__test_fill_${chunkIndex}`,
                        fakeData,
                      );
                      totalWritten += chunkSize;
                      chunkIndex++;
                      setFillProgress({
                        totalWritten,
                        chunkIndex,
                        targetBytes: bytesToFill,
                      });
                      // Small delay to allow UI updates
                      await new Promise((resolve) => setTimeout(resolve, 10));
                    } catch (e) {
                      if ((e as Error)?.name === "QuotaExceededError") {
                        setFillResult({
                          type: "quota_exceeded",
                          message: `✓ localStorage quota exceeded after ${chunkIndex} chunks (~${(totalWritten / 1024 / 1024).toFixed(2)}MB written)`,
                        });
                        refresh();
                        setIsFilling(false);
                        return;
                      }
                      throw e;
                    }
                  }

                  setFillResult({
                    type: "success",
                    message: `✓ Filled ~${mb}MB of localStorage (${chunkIndex} chunks)`,
                  });
                  refresh();
                  setIsFilling(false);
                } catch (e) {
                  setFillResult({
                    type: "error",
                    message: `Error: ${e instanceof Error ? e.message : String(e)}`,
                  });
                  setIsFilling(false);
                }
              }}
              disabled={isFilling}
              className="text-xs px-3 py-1 bg-amber-100 text-amber-900 rounded hover:bg-amber-200 disabled:bg-gray-100 disabled:text-gray-500 transition-colors font-medium"
            >
              {isFilling ? "Filling..." : "Fill localStorage"}
            </button>
            <button
              type="button"
              onClick={() => {
                setClearResult(null);
                setFillResult(null);
                const keys: string[] = [];
                for (let i = 0; i < localStorage.length; i++) {
                  const key = localStorage.key(i);
                  if (key?.startsWith("__test_fill_")) {
                    keys.push(key);
                  }
                }
                for (const key of keys) {
                  localStorage.removeItem(key);
                }
                setClearResult(`✓ Cleared ${keys.length} test items`);
                refresh();
              }}
              className="text-xs px-3 py-1 bg-red-100 text-red-900 rounded hover:bg-red-200 transition-colors font-medium"
            >
              Clear test data
            </button>
          </div>

          {isFilling && fillProgress && (
            <div className="bg-white border border-amber-300 rounded p-3 space-y-2">
              <div className="text-xs font-medium text-gray-700">
                Filling localStorage...
              </div>
              <div className="bg-gray-200 rounded-full h-2">
                <div
                  className="bg-amber-500 h-2 rounded-full transition-all"
                  style={{
                    width: `${Math.min((fillProgress.totalWritten / fillProgress.targetBytes) * 100, 100)}%`,
                  }}
                />
              </div>
              <div className="text-xs text-gray-600">
                {(fillProgress.totalWritten / 1024 / 1024).toFixed(2)}MB /{" "}
                {(fillProgress.targetBytes / 1024 / 1024).toFixed(2)}MB (
                {fillProgress.chunkIndex} chunks)
              </div>
            </div>
          )}

          {fillResult && (
            <div
              className={`rounded p-3 text-xs ${
                fillResult.type === "error"
                  ? "bg-red-50 text-red-700 border border-red-200"
                  : fillResult.type === "quota_exceeded"
                    ? "bg-amber-50 text-amber-700 border border-amber-200"
                    : "bg-green-50 text-green-700 border border-green-200"
              }`}
            >
              {fillResult.message}
            </div>
          )}

          {clearResult && (
            <div className="rounded p-3 text-xs bg-green-50 text-green-700 border border-green-200">
              {clearResult}
            </div>
          )}
        </div>
      </div>

      {/* Help Text */}
      <div className="text-xs text-gray-400 border-t pt-4 space-y-2">
        <p>
          <strong>Tip:</strong> If storage keeps growing over time, check the
          context dump in the Inspect tab to see what's accumulating in the
          machine state.
        </p>
        <p>
          Common culprits: previous user data not cleared, form responses
          retained, HRT/pricing cache not limited.
        </p>
      </div>
    </div>
  );
}
